import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPalette, FaSun, FaMoon, FaDesktop, FaCheck, FaTimes } from 'react-icons/fa';
import { useTheme, THEMES, THEME_MODES } from './ThemeContext';

export default function ThemeSwitcher() {
  const { mode, setMode, colorScheme, setColorScheme, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const canUseDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close dropdown on outside click (for desktop)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const modeOptions = [
    { id: THEME_MODES.dark, label: 'Dark', icon: FaMoon },
    { id: THEME_MODES.light, label: 'Light', icon: FaSun },
    { id: THEME_MODES.system, label: 'System', icon: FaDesktop },
  ];

  const ThemeContent = ({ isMobile = false }) => (
    <div className={isMobile ? 'flex flex-col h-full' : ''}>
      {/* Header */}
      <div className={`flex items-center justify-between gap-2 mb-4 pb-3 border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-neutral-200'}`}>
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${THEMES[colorScheme].preview[0]}, ${THEMES[colorScheme].preview[1]})`
            }}
          >
            <FaPalette className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-neutral-800'}`}>
              Theme Settings
            </h3>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-neutral-500'}`}>
              Customize appearance
            </p>
          </div>
        </div>
        {isMobile && (
          <button
            onClick={() => setIsOpen(false)}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-neutral-100 text-neutral-500'}`}
          >
            <FaTimes className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Mode Selector */}
      <div className="mb-4 flex-shrink-0">
        <label className={`text-xs font-medium mb-2 block ${isDark ? 'text-gray-400' : 'text-neutral-500'}`}>
          Mode
        </label>
        <div className={`grid grid-cols-3 gap-1.5 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-neutral-100'}`}>
          {modeOptions.map((option) => {
            const Icon = option.icon;
            const isActive = mode === option.id;
            return (
              <button
                key={option.id}
                onClick={() => setMode(option.id)}
                className={`
                  flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-xs font-medium
                  transition-all duration-200
                  ${isActive 
                    ? isDark 
                      ? 'bg-white/10 text-white' 
                      : 'bg-white text-neutral-800 shadow-sm'
                    : isDark 
                      ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-white/50'
                  }
                `}
              >
                <Icon className="w-3.5 h-3.5" />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Color Theme Selector */}
      <div className="flex-1 min-h-0 flex flex-col">
        <label className={`text-xs font-medium mb-2 block flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-neutral-500'}`}>
          Color Theme ({Object.keys(THEMES).length} options)
        </label>
        <div className={`space-y-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500/20 scrollbar-track-transparent pr-1 ${isMobile ? 'pb-2' : 'max-h-[280px]'}`}>
          {Object.values(THEMES).map((theme) => {
            const isActive = colorScheme === theme.id;
            return (
              <button
                key={theme.id}
                onClick={() => {
                  setColorScheme(theme.id);
                  if (isMobile) setIsOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-xl
                  transition-all duration-200 text-left
                  ${isActive 
                    ? isDark 
                      ? 'bg-white/10 border-2' 
                      : 'bg-neutral-100 border-2'
                    : isDark 
                      ? 'bg-white/5 hover:bg-white/10 border border-transparent' 
                      : 'bg-neutral-50 hover:bg-neutral-100 border border-transparent'
                  }
                `}
                style={{
                  borderColor: isActive ? theme.preview[1] : 'transparent'
                }}
              >
                {/* Color Preview */}
                <div className="flex -space-x-1">
                  {theme.preview.map((color, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 rounded-full border-2 border-white/20 shadow-sm"
                      style={{ backgroundColor: color, zIndex: 3 - i }}
                    />
                  ))}
                </div>

                {/* Theme Info */}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-neutral-800'}`}>
                    {theme.name}
                  </div>
                  <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-neutral-500'}`}>
                    {theme.description}
                  </div>
                </div>

                {/* Active Indicator */}
                {isActive && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: theme.preview[1] }}
                  >
                    <FaCheck className="w-3 h-3 text-white" />
                  </motion.div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className={`mt-4 pt-3 border-t flex-shrink-0 ${isDark ? 'border-white/10' : 'border-neutral-200'}`}>
        <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-neutral-400'}`}>
          Theme preference is saved automatically
        </p>
      </div>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          p-2.5 rounded-xl transition-all duration-300
          ${isDark 
            ? 'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 hover:border-white/20' 
            : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-600 hover:text-neutral-800 border border-neutral-200'
          }
        `}
        aria-label="Theme settings"
        title="Change theme"
      >
        <FaPalette className="w-4 h-4" />
      </button>

      {/* Desktop Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Desktop dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={`
                hidden sm:block absolute right-0 mt-2 w-80 rounded-2xl p-4 z-50
                border shadow-2xl
                ${isDark 
                  ? 'bg-slate-900/95 backdrop-blur-xl border-white/10' 
                  : 'bg-white/95 backdrop-blur-xl border-neutral-200'
                }
              `}
            >
              <ThemeContent isMobile={false} />
            </motion.div>

            {/* Mobile Bottom Sheet (portaled to body to avoid clipping in header/mobile menu) */}
            {canUseDOM && createPortal(
              <>
                <motion.div
                  key="theme-mobile-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="sm:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
                  onClick={() => setIsOpen(false)}
                />
                <motion.div
                  key="theme-mobile-sheet"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className={`
                    sm:hidden fixed bottom-0 left-0 right-0 z-[101]
                    rounded-t-3xl p-5 pb-8 max-h-[85vh] flex flex-col
                    ${isDark 
                      ? 'bg-slate-900 border-t border-white/10' 
                      : 'bg-white border-t border-neutral-200'
                    }
                  `}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Drag handle */}
                  <div className="flex justify-center mb-4 flex-shrink-0">
                    <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-neutral-300'}`} />
                  </div>
                  <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <ThemeContent isMobile={true} />
                  </div>
                </motion.div>
              </>,
              document.body
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
